import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Save, Plus, Trash2 } from 'lucide-react';
import { getToken } from '../../services/tokenService';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => ({
  'Authorization': `Bearer ${getToken()}`,
  'Content-Type': 'application/json'
});

const FooterEditor = () => {
  const [footerConfig, setFooterConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadFooter();
  }, []);

  const loadFooter = async () => {
    try {
      const response = await fetch(`${API_URL}/api/cms/footer`);
      const data = await response.json();
      setFooterConfig(data);
    } catch {
    }
    setLoading(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/cms/footer`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(footerConfig)
      });

      if (response.ok) {
        alert('✅ Footer mis à jour avec succès !');
      } else {
        alert('❌ Erreur lors de la mise à jour');
      }
    } catch {
      alert('❌ Erreur lors de la mise à jour');
    }
    setSaving(false);
  };

  const addNavigationLink = () => {
    const newNav = [...(footerConfig.navigation || [])];
    newNav.push({ label: 'Nouveau lien', url: '/nouveau' });
    setFooterConfig({ ...footerConfig, navigation: newNav });
  };

  const addSocialLink = () => {
    const newSocial = [...(footerConfig.social_links || [])];
    newSocial.push({ platform: 'linkedin', url: 'https://linkedin.com' });
    setFooterConfig({ ...footerConfig, social_links: newSocial });
  };

  if (loading) {
    return <Card><CardContent className="py-12 text-center">Chargement...</CardContent></Card>;
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle>Éditeur de Footer</CardTitle>
          <Button onClick={handleSave} disabled={saving}>
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Sauvegarde...' : 'Enregistrer'}
          </Button>
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Logo & Copyright */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2">URL du Logo</label>
            <Input
              value={footerConfig?.logo_url || ''}
              onChange={(e) => setFooterConfig({ ...footerConfig, logo_url: e.target.value })}
              placeholder="/path/to/logo.png"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Copyright</label>
            <Input
              value={footerConfig?.copyright || ''}
              onChange={(e) => setFooterConfig({ ...footerConfig, copyright: e.target.value })}
              placeholder="© 2025 COPPAM"
            />
          </div>
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium mb-2">Description</label>
          <Textarea
            value={footerConfig?.description || ''}
            onChange={(e) => setFooterConfig({ ...footerConfig, description: e.target.value })}
            placeholder="Description de l'organisation"
            rows={3}
          />
        </div>

        {/* Contact Info */}
        <div>
          <h3 className="text-lg font-semibold mb-3">Informations de Contact</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Email</label>
              <Input
                value={footerConfig?.contact?.email || ''}
                onChange={(e) => setFooterConfig({
                  ...footerConfig,
                  contact: { ...footerConfig.contact, email: e.target.value }
                })}
                placeholder="contact@coppam.fr"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Téléphone</label>
              <Input
                value={footerConfig?.contact?.phone || ''}
                onChange={(e) => setFooterConfig({
                  ...footerConfig,
                  contact: { ...footerConfig.contact, phone: e.target.value }
                })}
                placeholder="+33 1 23 45 67 89"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Adresse</label>
              <Input
                value={footerConfig?.contact?.address || ''}
                onChange={(e) => setFooterConfig({
                  ...footerConfig,
                  contact: { ...footerConfig.contact, address: e.target.value }
                })}
                placeholder="123 Rue, Paris"
              />
            </div>
          </div>
        </div>

        {/* Navigation Links */}
        <div>
          <div className="flex justify-between items-center mb-3">
            <h3 className="text-lg font-semibold">Liens de Navigation</h3>
            <Button onClick={addNavigationLink} size="sm">
              <Plus className="h-4 w-4 mr-2" />
              Ajouter
            </Button>
          </div>
          <div className="space-y-2">
            {footerConfig?.navigation?.map((link, index) => (
              <div key={index} className="flex gap-2">
                <Input
                  placeholder="Libellé"
                  value={link.label}
                  onChange={(e) => {
                    const newNav = [...footerConfig.navigation];
                    newNav[index].label = e.target.value;
                    setFooterConfig({ ...footerConfig, navigation: newNav });
                  }}
                />
                <Input
                  placeholder="URL"
                  value={link.url}
                  onChange={(e) => {
                    const newNav = [...footerConfig.navigation];
                    newNav[index].url = e.target.value;
                    setFooterConfig({ ...footerConfig, navigation: newNav });
                  }}
                />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    const newNav = footerConfig.navigation.filter((_, i) => i !== index);
                    setFooterConfig({ ...footerConfig, navigation: newNav });
                  }}
                >
                  <Trash2 className="h-4 w-4 text-red-500" />
                </Button>
              </div>
            ))}
          </div>
        </div>

        {/* Social Links */}
        <div>
          <div className="flex justify-between items-center mb-3">
            <h3 className="text-lg font-semibold">Réseaux Sociaux</h3>
            <Button onClick={addSocialLink} size="sm">
              <Plus className="h-4 w-4 mr-2" />
              Ajouter
            </Button>
          </div>
          <div className="space-y-2">
            {footerConfig?.social_links?.map((link, index) => (
              <div key={index} className="flex gap-2">
                <select
                  className="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={link.platform}
                  onChange={(e) => {
                    const newSocial = [...footerConfig.social_links];
                    newSocial[index].platform = e.target.value;
                    setFooterConfig({ ...footerConfig, social_links: newSocial });
                  }}
                >
                  <option value="linkedin">LinkedIn</option>
                  <option value="twitter">Twitter</option>
                  <option value="facebook">Facebook</option>
                  <option value="instagram">Instagram</option>
                  <option value="youtube">YouTube</option>
                </select>
                <Input
                  placeholder="URL"
                  value={link.url}
                  onChange={(e) => {
                    const newSocial = [...footerConfig.social_links];
                    newSocial[index].url = e.target.value;
                    setFooterConfig({ ...footerConfig, social_links: newSocial });
                  }}
                  className="flex-1"
                />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    const newSocial = footerConfig.social_links.filter((_, i) => i !== index);
                    setFooterConfig({ ...footerConfig, social_links: newSocial });
                  }}
                >
                  <Trash2 className="h-4 w-4 text-red-500" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default FooterEditor;